/**
 * Microphone acquisition that survives the real world.
 *
 * The old path asked for one set of constraints, and if the browser said no
 * for any reason other than OverconstrainedError, that was the end of it.
 * Three things break that:
 *
 *   1. A CONFERENCING APP IS RUNNING. Zoom, Teams and Meet install virtual
 *      audio devices and — on some systems — hold the real one. The browser
 *      then either refuses (NotReadableError) or, far worse, hands back a
 *      LIVE STREAM THAT IS DIGITALLY SILENT. Nothing was broken as far as
 *      the old code could see: permission granted, track live, every sample
 *      zero. The singer just looked like they were not singing.
 *   2. THE DEFAULT DEVICE CHANGES MID-SESSION. Joining or leaving a Zoom
 *      call reassigns the system input. A MediaStream captured before the
 *      switch keeps pointing at a device that is no longer producing audio.
 *   3. BROWSER SPREAD. Safari needs the AudioContext resumed after the
 *      stream is attached; older Safari only has the prefixed API; an
 *      insecure origin has no navigator.mediaDevices at all, which reads to
 *      the user as "this browser is unsupported" when it is really "this
 *      page is not on HTTPS".
 *
 * So: a retry ladder, device enumeration with a remembered choice, a
 * silence watchdog, and live recovery when the device list changes.
 */

export interface MicDevice {
  deviceId: string;
  label: string;
  /** a conferencing/loopback device — usually silent to the browser */
  virtual: boolean;
}

export interface OpenMicResult {
  stream: MediaStream;
  deviceId: string | null;
  label: string;
  /** shown to the singer when the mic works but something is worth knowing */
  warning: string | null;
}

const PREF_KEY = 'sss-mic-device-v1';

/**
 * Devices that route another app's audio rather than a microphone. Zoom's is
 * the one that bites: on macOS "ZoomAudioDevice" is frequently the default
 * input while a call is running, and it delivers silence to everyone else.
 */
const VIRTUAL_HINTS = [
  'zoom', 'teams', 'microsoft teams', 'google meet', 'webex', 'skype',
  'blackhole', 'soundflower', 'loopback', 'obs', 'virtual', 'vb-audio',
  'voicemeeter', 'aggregate', 'multi-output', 'stream engine', 'ndi',
];

export function isVirtualLabel(label: string): boolean {
  const l = label.toLowerCase();
  return VIRTUAL_HINTS.some((h) => l.includes(h));
}

export function loadPreferredMic(): string | null {
  try { return localStorage.getItem(PREF_KEY); } catch { return null; }
}

export function savePreferredMic(id: string | null): void {
  try {
    if (id) localStorage.setItem(PREF_KEY, id);
    else localStorage.removeItem(PREF_KEY);
  } catch { /* a preference, not a promise */ }
}

/** getUserMedia across browsers, including the pre-2018 Safari/Chrome shape. */
function getUserMediaCompat(constraints: MediaStreamConstraints): Promise<MediaStream> {
  if (navigator.mediaDevices?.getUserMedia) {
    return navigator.mediaDevices.getUserMedia(constraints);
  }
  const legacy = navigator as unknown as {
    getUserMedia?: LegacyGum; webkitGetUserMedia?: LegacyGum; mozGetUserMedia?: LegacyGum;
  };
  const fn = legacy.getUserMedia ?? legacy.webkitGetUserMedia ?? legacy.mozGetUserMedia;
  if (!fn) return Promise.reject(new Error('NO_GUM'));
  return new Promise((resolve, reject) => fn.call(navigator, constraints, resolve, reject));
}

type LegacyGum = (
  c: MediaStreamConstraints,
  ok: (s: MediaStream) => void,
  err: (e: unknown) => void,
) => void;

/** Why can this page not even ask for a microphone? */
export function unsupportedReason(): string | null {
  const secure = window.isSecureContext
    || location.protocol === 'https:'
    || location.hostname === 'localhost'
    || location.hostname === '127.0.0.1';
  if (!secure) {
    return 'Microphones only work on a secure connection. Open this page at its https:// address and try again.';
  }
  if (!navigator.mediaDevices?.getUserMedia
    && !(navigator as unknown as { webkitGetUserMedia?: unknown }).webkitGetUserMedia) {
    return 'This browser will not give a web page microphone access. Chrome, Edge, Firefox and Safari all will.';
  }
  // Inside an iframe, the EMBEDDING page decides whether the mic even exists
  // for us: without allow="microphone" on the iframe, getUserMedia throws
  // NotAllowedError no matter what the student granted in the browser — which
  // used to surface here as "permission was refused", sending people on a
  // wild goose chase through the padlock menu and System Settings.
  if (window.self !== window.top) {
    const doc = document as unknown as {
      permissionsPolicy?: { allowsFeature?: (f: string) => boolean };
      featurePolicy?: { allowsFeature?: (f: string) => boolean };
    };
    const policy = doc.permissionsPolicy ?? doc.featurePolicy;
    if (policy?.allowsFeature && !policy.allowsFeature('microphone')) {
      return 'The page this lesson is embedded in has not passed microphone access through yet. '
        + 'Reload the page — if this message stays, the school site needs a small update, '
        + 'and your browser settings are not the problem.';
    }
  }
  return null;
}

/**
 * The inputs this machine can offer. Labels are only populated once
 * permission has been granted at least once, which is why the picker in the
 * sound check calls this AFTER opening the mic.
 */
export async function listMicDevices(): Promise<MicDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all
      .filter((d) => d.kind === 'audioinput')
      .map((d, i) => ({
        deviceId: d.deviceId,
        label: d.label || `Microphone ${i + 1}`,
        virtual: isVirtualLabel(d.label || ''),
      }));
  } catch {
    return [];
  }
}

/** The processing chain that fights pitch detection, switched off. */
const CLEAN: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

function labelOf(stream: MediaStream): string {
  return stream.getAudioTracks()[0]?.label || 'Microphone';
}

function deviceIdOf(stream: MediaStream): string | null {
  const s = stream.getAudioTracks()[0]?.getSettings?.();
  return s?.deviceId ?? null;
}

/**
 * Open the microphone, trying progressively less demanding requests.
 *
 * The ladder matters: a device held by another app fails on an exact
 * deviceId but frequently succeeds on a bare `{audio:true}`, because the
 * browser is then free to hand over whichever input is actually available.
 */
export async function openMic(preferredId?: string | null): Promise<OpenMicResult> {
  const blocked = unsupportedReason();
  if (blocked) throw new Error(blocked);

  const attempts: MediaStreamConstraints[] = [];
  if (preferredId) {
    attempts.push({ audio: { deviceId: { exact: preferredId }, ...CLEAN } });
    attempts.push({ audio: { deviceId: preferredId } });
  }
  attempts.push({ audio: CLEAN });
  attempts.push({ audio: true });

  let lastError: unknown = null;
  for (const constraints of attempts) {
    try {
      const stream = await getUserMediaCompat(constraints);
      if (stream.getAudioTracks().length) {
        return finish(stream, preferredId);
      }
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      lastError = e;
      const name = (e as DOMException)?.name;
      // A refusal is final — retrying cannot talk the user into saying yes.
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
        throw new Error(describeMicError(e));
      }
    }
  }

  // Last resort: walk the real (non-virtual) inputs one at a time. This is
  // what rescues a machine whose DEFAULT input is a conferencing device.
  const devices = await listMicDevices();
  for (const d of [...devices].sort((a, b) => Number(a.virtual) - Number(b.virtual))) {
    if (!d.deviceId) continue;
    try {
      const stream = await getUserMediaCompat({ audio: { deviceId: { exact: d.deviceId } } });
      if (stream.getAudioTracks().length) return finish(stream, preferredId);
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) { lastError = e; }
  }

  throw new Error(describeMicError(lastError));
}

function finish(stream: MediaStream, preferredId?: string | null): OpenMicResult {
  const label = labelOf(stream);
  const deviceId = deviceIdOf(stream);
  let warning: string | null = null;
  if (isVirtualLabel(label)) {
    warning = `“${label}” is a conferencing device, not a real microphone — it usually sends silence to a web page. `
      + 'Pick your actual mic below if nothing registers.';
  } else if (preferredId && deviceId && deviceId !== preferredId) {
    warning = `Your usual microphone was not available, so the studio opened “${label}” instead.`;
  }
  return { stream, deviceId, label, warning };
}

export function describeMicError(error: unknown): string {
  const name = (error as DOMException)?.name;
  const msg = (error as Error)?.message ?? '';
  if (msg === 'NO_GUM') {
    return 'This browser will not give a web page microphone access. Chrome, Edge, Firefox and Safari all will.';
  }
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Microphone permission was refused. Click the padlock (or camera icon) in the address bar, '
        + 'allow the microphone for this site, then reload. On a Mac also check System Settings → '
        + 'Privacy & Security → Microphone for your browser.';
    case 'SecurityError':
      return 'This page is not allowed to use the microphone. Open it at its https:// address and try again.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found. Plug one in — or if you are on a Mac with an external interface, '
        + 'check it is selected in System Settings → Sound → Input.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Another app is holding the microphone. Zoom, Teams and Meet do this while a call is open: '
        + 'mute yourself there (or leave the call), then press Sing it again. '
        + 'Nothing needs reloading — the studio will pick the mic up.';
    case 'AbortError':
      return 'The microphone stopped responding. Unplug and replug it, or pick a different input below.';
    case 'OverconstrainedError':
      return 'That microphone could not run in the mode the studio asked for. Pick a different input below.';
    default:
      return 'Could not open the microphone. If you are in a Zoom or Teams call, mute yourself there and try again; '
        + 'otherwise pick a different input below.';
  }
}

/**
 * Watch for the system input changing (a call starting or ending is the
 * common cause) and tell the caller so it can re-open the mic. Returns an
 * unsubscribe function.
 */
export function onDeviceChange(cb: () => void): () => void {
  if (!navigator.mediaDevices?.addEventListener) return () => {};
  navigator.mediaDevices.addEventListener('devicechange', cb);
  return () => navigator.mediaDevices.removeEventListener('devicechange', cb);
}
